import { FC, useState } from 'react';
import { useParams } from 'react-router';
import { Link } from 'react-router-dom';
import { prop } from 'ramda';

import {
  isFetchingMore,
  isLoading,
  isRefetching,
} from 'common/graphql/networkStatus';
import Button from 'components/_core/Button';
import SunshineCard from 'components/_core/SunshineCard';
import Text from 'components/_core/Text';
import Initial from 'components/Initial';
import OldIbanBadge from 'components/OldIbanBadge';
import useGetMigrationToEpData from 'features/Bank/pages/Overview/Transactions/hooks/useGetMigrationToEPData';
import * as ContentLayout from 'layouts/ContentLayout';

import DirectDebitEmptyView from './DirectDebitEmptyView';
import DirectDebitMandateBadge from './DirectDebitMandateBadge';
import DirectDebitMandateOldIbanWarning from './DirectDebitMandateOldIbanWarning';
import DirectDebitMandateSidebar from './DirectDebitMandateSidebar';
import FlatList from './FlatList';
import { useDirectDebitMandateCountsQuery } from './hooks/useDirectDebitMandateCountsQuery';
import {
  DirectDebitMandate,
  useDirectDebitMandatesQuery,
} from './hooks/useDirectDebitMandatesQuery';
import locales from './locales';
import * as S from './styles';

const getListTitle = (title: string, count?: number): string =>
  count !== undefined ? `${title} (${count})` : title;

interface DirectDebitMandateListParams {
  directDebitMandateId: string;
}

const DirectDebitMandateList: FC = () => {
  const { directDebitMandateId: selectedDirectDebitMandateId } =
    useParams<DirectDebitMandateListParams>();

  const { hasBeenSevenDaysSinceMigrationStarted } = useGetMigrationToEpData();

  const [isSuspendedListCollapsed, setIsSuspendedListCollapsed] =
    useState<boolean>(true);

  const {
    directDebitMandates: activeDirectDebitMandates,
    error: errorActiveMandates,
    fetchMore: fetchMoreActiveMandates,
    networkStatus: networkStatusActiveMandates,
    pageInfo: pageInfoActiveMandates,
    refetch: refetchActiveMandates,
  } = useDirectDebitMandatesQuery({
    isRevoked: false,
  });
  const {
    directDebitMandates: suspendedDirectDebitMandates,
    error: errorSuspendedMandates,
    fetchMore: fetchMoreSuspendedMandates,
    networkStatus: networkStatusSuspendedMandates,
    pageInfo: pageInfoSuspendedMandates,
    refetch: refetchSuspendedMandates,
  } = useDirectDebitMandatesQuery({ isRevoked: true });

  const { activeCount, suspendedCount } = useDirectDebitMandateCountsQuery();

  const renderItem = (directDebitMandate: DirectDebitMandate) => {
    const {
      creditorName,
      directDebitMandateId,
      isOldDebtorIban,
      isRevoked,
      label,
    } = directDebitMandate;
    const isSelected = directDebitMandateId === selectedDirectDebitMandateId;
    const name = label || creditorName;

    return (
      <SunshineCard
        as={Link}
        isActive={isSelected}
        to={`/payments/mandates/${directDebitMandateId}`}
      >
        <SunshineCard.Content
          alignItems="center"
          display="flex"
          flexDirection="row"
          paddingY="0"
        >
          <Initial name={name} />
          <S.ItemMiddle>
            <Text fontWeight="weight-500" variant="primary">
              {name}
            </Text>
            <Text size="sm" variant="light">
              {!isRevoked
                ? locales.authorizedMandate
                : locales.suspendedMandate}
              {hasBeenSevenDaysSinceMigrationStarted && isOldDebtorIban ? (
                <OldIbanBadge />
              ) : null}
            </Text>
          </S.ItemMiddle>
          <S.ItemBadge>
            <DirectDebitMandateBadge isRevoked={isRevoked} />
          </S.ItemBadge>
        </SunshineCard.Content>
      </SunshineCard>
    );
  };

  const hasDirectDebitMandatesWithOldIban = activeDirectDebitMandates.some(
    ({ isOldDebtorIban }) => isOldDebtorIban,
  );

  return (
    <ContentLayout.Root>
      <ContentLayout.Body>
        <S.TitleContainer>
          <S.Title>{locales.title}</S.Title>
          <Button
            as={Link}
            icon="plus"
            iconPosition="left"
            to="/payments/mandates/add"
            variant="secondary"
          >
            {locales.addMandateButton}
          </Button>
        </S.TitleContainer>
        {hasDirectDebitMandatesWithOldIban ? (
          <S.DirectDebitMandateOldIbanWarningContainer>
            <DirectDebitMandateOldIbanWarning
              hasMoreThanOneDeprecatedMandates
            />
          </S.DirectDebitMandateOldIbanWarningContainer>
        ) : null}
        <S.ListContainer>
          <FlatList<DirectDebitMandate>
            data={activeDirectDebitMandates}
            EmptyComponent={<DirectDebitEmptyView isRevoked={false} />}
            error={errorActiveMandates}
            fetchMore={fetchMoreActiveMandates}
            getKey={prop('directDebitMandateId')}
            hasNextPage={pageInfoActiveMandates?.hasNextPage}
            isFetchingMore={isFetchingMore(networkStatusActiveMandates)}
            isLoading={
              isLoading(networkStatusActiveMandates) ||
              isRefetching(networkStatusActiveMandates)
            }
            refetch={refetchActiveMandates}
            renderItem={renderItem}
            title={getListTitle(locales.activeMandateTitle, activeCount)}
          />
        </S.ListContainer>
        <S.ListContainer>
          <FlatList<DirectDebitMandate>
            data={suspendedDirectDebitMandates}
            EmptyComponent={<DirectDebitEmptyView isRevoked />}
            error={errorSuspendedMandates}
            fetchMore={fetchMoreSuspendedMandates}
            getKey={prop('directDebitMandateId')}
            hasNextPage={pageInfoSuspendedMandates?.hasNextPage}
            isCollapsed={isSuspendedListCollapsed}
            isFetchingMore={isFetchingMore(networkStatusSuspendedMandates)}
            isLoading={
              isLoading(networkStatusSuspendedMandates) ||
              isRefetching(networkStatusSuspendedMandates)
            }
            onCollapse={() =>
              setIsSuspendedListCollapsed(!isSuspendedListCollapsed)
            }
            refetch={refetchSuspendedMandates}
            renderItem={renderItem}
            title={getListTitle(locales.suspendedMandateTitle, suspendedCount)}
          />
        </S.ListContainer>
      </ContentLayout.Body>
      {selectedDirectDebitMandateId ? (
        <ContentLayout.Sidebar isScrollable>
          <DirectDebitMandateSidebar />
        </ContentLayout.Sidebar>
      ) : null}
    </ContentLayout.Root>
  );
};

export default DirectDebitMandateList;
